import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faAngleRight, faRefresh, faWandSparkles, faXmark} from "@fortawesome/free-solid-svg-icons";
import {faUser} from "@fortawesome/free-regular-svg-icons";

import { Button, Loader, StarRating } from "../../../components";
import {AXIOS} from "../../../services/api";

import styles from "./ReviewItem.module.scss";

function ReviewItem({review = {}, fetchReviews}) {
    const [humanReply, setHumanReply] = React.useState('');
    const [aiReply, setAiReply] = React.useState('');
    const [reply, setReply] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [isGeneratingAIResponse, setIsGeneratingAIResponse] = React.useState(false);

    async function generateAIResponse() {
        let reply = review.replySuggestionOriginal?.trim();

        if (reply) {
            setAiReply(reply);
            setReply(reply);
            return;
        }

        await refreshAIResponse();
    }

    async function refreshAIResponse() {
        setIsGeneratingAIResponse(true);
        const response = await AXIOS.get(`/applications/${review.applicationId}/reviews/${review.id}/reply-suggestion`);
        review = response.data.data;

        setAiReply(review.replySuggestionOriginal);
        setReply(review.replySuggestionOriginal);

        setIsGeneratingAIResponse(false);
    }

    function generateHumanResponse(reply) {
        reply = reply.trim();
        if (reply) {
            setHumanReply(reply);
            setReply(reply);
        } else {
            setHumanReply('');
            setReply('');
        }
    }

    function cancelHumanResponse() {
        setHumanReply('');
        setReply('');
    }

    function cancelAIResponse() {
        setAiReply('');
        setReply('');
    }

    async function respondToReview() {
        setIsLoading(true);
        try {
            await AXIOS.post(`/applications/${review.applicationId}/reviews/${review.id}/respond`, {reply});
            setHumanReply('');
            setAiReply('');
            setReply('');
            fetchReviews(); // Fetch reviews again after responding
        } catch (error) {
            setError('Error responding to review');
        } finally {
            setIsLoading(false);
        }
    }

    if (isLoading) {
        return <Loader loading={isLoading} />
    }

    if (error) {
        return <div>{error}</div>;
    }

    // Format date
    const formattedDate = new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).format(new Date(review.reviewSubmittedAt));

    return (
        <div className={styles["review-item"]}>
            <div className={styles["review-header"]}>
                <div className={styles["avatar"]}>
                    <FontAwesomeIcon icon={faUser} className="icon" size="2x"/>
                </div>
                <div className={styles["reviewer-info"]}>
                    <div className={styles["top-row"]}>
                        <h4>{review.authorName}</h4>
                        <span className={styles["stars"]}>
                            <StarRating rating={review.rating} />
                        </span>
                    </div>
                    <span className={styles["date"]}>{formattedDate}</span>
                </div>
            </div>
            <p className={styles["review-text"]}>
                {review.reviewText}
            </p>

            {review.replyText && <p className={styles["reply-text"]}>{review.replyText}</p>}

            <div className={styles["reply-container"]}>
                <input
                    type="text"
                    id="humanReply"
                    name="humanReply"
                    placeholder="Enter Reply"
                    value={humanReply}
                    onChange={(e) => generateHumanResponse(e.target.value)}
                    className={styles["reply-input"]}
                />
                <button
                    className={styles.button}
                    onClick={generateAIResponse}
                    title="Generate AI Response"
                    type="button"
                >
                    <FontAwesomeIcon icon={faWandSparkles} />
                </button>
                {humanReply && (
                    <button
                        className={styles.button}
                        onClick={cancelHumanResponse}
                        title="Cancel"
                        type="button"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
                <button
                    className={styles.button}
                    onClick={respondToReview}
                    title="Send"
                    type="button"
                    disabled={!humanReply}
                >
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>

            {/* AI reply preview and actions (if needed) */}
            {aiReply && (
                <div className={styles["ai-response"]}>
                    <div className={styles["ai-content"]}>
                        <p>{isGeneratingAIResponse ? "Generating AI Response..." : aiReply}</p>
                    </div>
                    <div className={styles["ai-actions"]}>
                        <button
                            className={styles.button}
                            onClick={cancelAIResponse}
                            title="Cancel AI Response"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                        <button
                            className={styles.button}
                            onClick={refreshAIResponse}
                            title="Regenerate AI Response"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                        </button>
                        <button
                            className={styles.button}
                            onClick={respondToReview}
                            title="Send AI Reply"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faAngleRight} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export {ReviewItem};
